import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react"
import bbApi from "../../../api"
import { pushToast } from "../../../redux/slices/notifications"
import { AutocompleteOption, GooglePlace } from "../../../types"
import { useAppDispatch } from "../../hooks"
import Autocomplete from "../form/autocomplete"
import {
  convertAddressObjToPlaceString,
  convertGooglePlaceToAddressObj
} from "../../utils"

interface Props {
  name: string
  label: string
  placeholder: string
  initialValue?: string
  initialInputText?: string
  required?: boolean
  onChange: (value: GooglePlace | undefined) => void
}

const AddressAutocomplete: FC<Props> = ({
  name,
  label,
  placeholder,
  initialValue,
  initialInputText = "",
  required = false,
  onChange
}) => {
  const dispatch = useAppDispatch()
  const onChangeRef = useRef(onChange)

  const [placeQuery, setPlaceQuery] = useState<string>("")
  const [debouncedPlaceQuery, setDebouncedPlaceQuery] = useState<string>("")
  const [placeOptions, setPlaceOptions] = useState<AutocompleteOption[]>([])
  const [selectedPlace, setSelectedPlace] = useState<GooglePlace>()
  const [isManuallyCleared, setIsManuallyCleared] = useState<boolean>(false)
  const [pendingOption, setPendingOption] = useState<
    { id: string; text: string } | undefined
  >(
    initialValue
      ? { id: initialValue, text: initialInputText || initialValue }
      : undefined
  )

  // Update the ref when onChange changes
  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  // Debounce place query to reduce API calls
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedPlaceQuery(placeQuery)
    }, 200) // 200ms debounce delay

    return () => clearTimeout(timeoutId)
  }, [placeQuery])

  const autoCompleteOptions = useMemo(
    () =>
      placeOptions?.map?.(({ place_id, description }) => ({
        id: place_id,
        text: description
      })) ?? [],
    [placeOptions]
  )

  const placeAutoComplete = useCallback(
    async (input: string) => {
      try {
        if (!input || input.length < 2) {
          return []
        }
        const data = await bbApi.brand.placeAutoComplete({ input })
        setPlaceOptions(data)
      } catch (error) {
        console.error(error)
      }
    },
    [setPlaceOptions]
  )

  const setCurrentPlace = useCallback(
    async (placeId: string) => {
      if (!placeId || placeId === "") {
        setSelectedPlace(undefined)
        setPlaceQuery("")
        setIsManuallyCleared(true)
        setPendingOption(undefined)
        onChangeRef.current(undefined)
        return
      }

      try {
        const place = await bbApi.brand.getPlace({
          place_id: placeId
        })

        console.log("place", place)

        if ("status" in place) {
          dispatch(
            pushToast({
              msg: "Google place API is unavailble, try again later",
              timeout: 3000
            })
          )
        } else {
          setSelectedPlace(place)
          setIsManuallyCleared(false)
        }
      } catch (error) {
        console.error(error)
      }
    },
    [dispatch, setSelectedPlace]
  )

  useEffect(() => {
    placeAutoComplete(debouncedPlaceQuery)
  }, [placeAutoComplete, debouncedPlaceQuery])

  useEffect(() => {
    if (selectedPlace?.place_id) {
      onChangeRef.current(selectedPlace)
    }
  }, [selectedPlace])

  // useEffect(() => {
  //   if (initialValue) {
  //     // Only fetch place details if we don't have initialInputText
  //     // If we have initialInputText, we already have the display text
  //     if (!initialInputText) {
  //       setCurrentPlace(initialValue)
  //     }
  //   }
  // }, [initialValue, initialInputText, setCurrentPlace])

  useEffect(() => {
    return () => {
      setSelectedPlace(undefined)
      setPlaceQuery("")
      setPlaceOptions([])
      setIsManuallyCleared(false)
      setPendingOption(undefined)
    }
  }, [setPlaceOptions, setPlaceQuery, setSelectedPlace])

  const selectedOption = useMemo(() => {
    if (isManuallyCleared) return { id: "", text: "" }

    if (selectedPlace?.place_id) {
      const hydrated =
        convertAddressObjToPlaceString(
          convertGooglePlaceToAddressObj(selectedPlace)
        ) ||
        pendingOption?.text ||
        initialInputText

      return { id: selectedPlace.place_id, text: hydrated }
    }

    // Before place details are fetched, keep showing the option the user picked
    return pendingOption ?? { id: "", text: "" }
  }, [selectedPlace, pendingOption, initialInputText, isManuallyCleared])

  const onChangeOption = useCallback(
    (option: { id: string; text: string }) => {
      setPendingOption(option)
      setCurrentPlace(option.id)
    },
    [setCurrentPlace]
  )

  return (
    <Autocomplete
      required={required}
      label={label}
      name={name}
      placeholder={placeholder}
      value={selectedOption}
      query={placeQuery}
      options={autoCompleteOptions}
      onChange={onChangeOption}
      onChangeQuery={setPlaceQuery}
    />
  )
}

export default AddressAutocomplete
